import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import { Activity, CalendarClock, Loader2, Video, X } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useUser } from '../../../../contexts/UserContext';
import { useFetchDashboard } from '../../../../services/dashboard';
import { useSaveUiState, useUiState } from '../../../../services/uiState';

// Upcoming calls and Activity (Nic 2026-10-09): two compact buttons beside
// the kanban's search that pop out the same lists, instead of two panels
// taking the top of the page.

const SOON_MS = 10 * 60 * 1000;

const ACTIVITY_LABEL = {
  'claim:created': () => 'created a new claim',
  'claim:updated': () => 'updated their claim',
  'file:created': (row) => `uploaded ${row.fileName}`,
  'file:updated': (row) => `uploaded a new version of ${row.fileName}`,
};

// Unread, not a total (Nic 2026-10-09). The feed's dates are days only
// (claimUpdatedOn and fileLastUpdated are DATE columns), so "since you last
// looked" can't be a timestamp: we remember WHICH rows you've been shown.
// Saved on the server per person (migration 210), so it survives sign-out
// and follows you to any computer. Opening the panel marks everything seen.
// A first visit starts with everything seen (0, not 30).
const SEEN_MAX = 300;
const keyOf = (row) =>
  [row.type, row.claimId, row.date, row.fileName || '', row.fileVersion ?? ''].join('|');
// Before the server kept it, this browser did: carry that list over once.
const readOldLocalSeen = (userId) => {
  try {
    const raw = localStorage.getItem(`kanbanActivitySeen:${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// One pop-out: the button, and its panel anchored under it. Escape or a
// click outside closes it. The panel always fits the window (Nic
// 2026-10-09): kept inside the left and right edges, and its list never runs
// past the bottom.
const PANEL_W = 440;
const EDGE = 16;
const HEADER_H = 50;
const QuickPanel = ({ icon: Icon, label, count, alert = false, title, headerRight = null, onOpen = null, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [place, setPlace] = useState(null);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const fit = () => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(PANEL_W, window.innerWidth - EDGE * 2);
      const left = Math.min(Math.max(r.left, EDGE), window.innerWidth - width - EDGE);
      const top = r.bottom + 8;
      setPlace({ left, top, width, listMax: Math.max(160, window.innerHeight - top - EDGE - HEADER_H) });
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('scroll', fit, true);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('scroll', fit, true);
    };
  }, [open]);
  useEffect(() => {
    if (open && onOpen) onOpen();
    // Once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'inline-flex h-[34px] items-center gap-2 whitespace-nowrap rounded-lg px-[12px] text-[13px] font-semibold outline-none ring-1 focus:outline-none',
          open
            ? 'bg-slate-900 text-white ring-slate-900 dark:!bg-slate-100 dark:!text-slate-900 dark:ring-slate-100'
            : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10 dark:hover:!bg-white/10',
        )}
      >
        <Icon size={15} className="shrink-0" />
        {label}
        {count != null ? (
          <span
            className={cn(
              'min-w-[20px] rounded-full px-1.5 text-center text-[11px] tabular-nums',
              alert
                ? 'bg-rose-500 text-white'
                : open
                  ? 'bg-white/20'
                  : 'bg-slate-900/[0.07] text-slate-600 dark:!bg-white/10 dark:text-slate-300',
            )}
          >
            {count}
          </span>
        ) : null}
      </button>
      {open && place ? (
        <div
          style={{ left: place.left, top: place.top, width: place.width }}
          className="fixed z-[1050] overflow-hidden rounded-2xl bg-white shadow-[0_12px_40px_rgba(15,23,42,0.18)] ring-1 ring-slate-200 dark:!bg-surface-elevated dark:ring-white/10"
        >
          <div className="flex items-center gap-2 border-b border-slate-100 px-[16px] py-3 dark:border-white/10">
            <Icon size={15} className="shrink-0 text-slate-500 dark:text-slate-400" />
            <div className="min-w-0 flex-1 text-sm font-bold text-slate-800 dark:text-slate-100">{title}</div>
            {headerRight}
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-white"
            >
              <X size={15} />
            </button>
          </div>
          <div
            className="os-scroll overflow-y-auto overflow-x-hidden"
            style={{ maxHeight: Math.min(420, place.listMax) }}
          >
            {children}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Empty = ({ children }) => (
  <div className="px-[16px] py-8 text-center text-sm text-slate-400 dark:text-slate-500">{children}</div>
);

const ActivityBar = () => {
  const { user } = useUser();
  const { isLoading, data } = useFetchDashboard();
  const ui = useUiState();
  const saveUiState = useSaveUiState();
  const invites = data?.meetingInvites || [];
  const activity = useMemo(() => data?.activity || [], [data?.activity]);

  const seen = ui.data?.kanbanActivitySeen || null;
  const saveSeen = (list) => saveUiState('kanbanActivitySeen', list.slice(-SEEN_MAX));
  // The rows that were new when the panel opened, so they stay marked
  // while you read them.
  const [freshKeys, setFreshKeys] = useState(() => new Set());
  const keys = useMemo(() => activity.map(keyOf), [activity]);
  useEffect(() => {
    if (isLoading || ui.isLoading || ui.isError || seen) return;
    // First visit: this browser's old list if it had one, else what's there now.
    saveSeen(readOldLocalSeen(user?.userId) || keys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, ui.isLoading, ui.isError, seen, keys]);
  const seenSet = useMemo(() => new Set(seen || keys), [seen, keys]);
  const unread = keys.filter((k) => !seenSet.has(k)).length;
  const markSeen = () => {
    setFreshKeys(new Set(keys.filter((k) => !seenSet.has(k))));
    saveSeen([...new Set([...(seen || []), ...keys])]);
  };
  const now = Date.now();
  const ahead = invites.filter((i) => new Date(i.meetingInviteDate).getTime() > now);
  const soon = ahead.some((i) => new Date(i.meetingInviteDate).getTime() - now < SOON_MS);

  return (
    <div className="flex items-center gap-2">
      <QuickPanel
        icon={CalendarClock}
        label="Upcoming calls"
        count={isLoading ? null : ahead.length}
        alert={soon}
        title="Upcoming calls"
        headerRight={
          <Link to="/calendar" className="text-xs font-semibold no-underline">
            Your calendar
          </Link>
        }
      >
        {isLoading ? (
          <Empty>
            <Loader2 size={16} className="mx-auto animate-spin" />
          </Empty>
        ) : invites.length === 0 ? (
          <Empty>No meetings in the next 60 days.</Empty>
        ) : (
          <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/10">
            {invites.map((invite) => {
              const diff = new Date(invite.meetingInviteDate).getTime() - now;
              return (
                <li key={invite.meetingInviteId} className="flex items-center gap-3 px-[16px] py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {invite.clientUserFirstName} {invite.clientUserLastName}
                    </div>
                    <div
                      className={cn(
                        'text-xs tabular-nums',
                        diff < 0
                          ? 'text-slate-400'
                          : diff < SOON_MS
                            ? 'font-semibold text-rose-600 dark:text-rose-300'
                            : 'text-slate-500 dark:text-slate-400',
                      )}
                    >
                      {moment(invite.meetingInviteDate).format('ddd MMM D, h:mm A')}
                    </div>
                  </div>
                  <Link
                    to={`/meeting/${invite.meetingInviteId}`}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-sky-600 px-[12px] text-xs font-semibold text-white no-underline hover:bg-sky-500 hover:text-white"
                  >
                    <Video size={13} /> Join
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </QuickPanel>

      <QuickPanel
        icon={Activity}
        label="Activity"
        count={isLoading || !unread ? null : unread}
        title="Recent activity"
        onOpen={markSeen}
      >
        {isLoading ? (
          <Empty>
            <Loader2 size={16} className="mx-auto animate-spin" />
          </Empty>
        ) : activity.length === 0 ? (
          <Empty>No new activity.</Empty>
        ) : (
          <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/10">
            {activity.map((row, idx) => (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                className="flex gap-3 px-[16px] py-2.5 text-sm"
              >
                {/* New since you last looked: a quiet dot, not a fill. */}
                <span className="flex w-[8px] shrink-0 justify-center pt-[7px]">
                  {freshKeys.has(keyOf(row)) ? (
                    <span className="h-[6px] w-[6px] rounded-full bg-sky-500" aria-label="New" />
                  ) : null}
                </span>
                <span className="w-[56px] shrink-0 text-xs tabular-nums text-slate-400">
                  {moment(row.date).format('MMM D')}
                </span>
                <Link
                  to={`/admin/profile/${row.claimId}`}
                  // Long file names wrap anywhere: never a sideways scroll.
                  className="min-w-0 flex-1 text-slate-700 no-underline [overflow-wrap:anywhere] hover:underline dark:text-slate-200"
                >
                  <span className="font-semibold">
                    {row.userFirstName} {row.userLastName}
                  </span>{' '}
                  {(ACTIVITY_LABEL[row.type] || (() => ''))(row)}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </QuickPanel>
    </div>
  );
};

export default ActivityBar;
